import type { SimpleFieldConfig } from "../../../types/index";
import { looksLikeUuid } from "./field-kind";

// ============================================================================
// FILTER DISPLAY HELPERS
// ============================================================================

/** Format a `{ min, max }` range into a compact, human string. */
function formatRange(value: unknown): string {
  const range = value && typeof value === "object" ? (value as { min?: unknown; max?: unknown }) : {};
  const min = range.min !== null && range.min !== undefined && range.min !== "" ? String(range.min) : "";
  const max = range.max !== null && range.max !== undefined && range.max !== "" ? String(range.max) : "";

  if (!min && !max) return "Any";
  if (min && !max) return `From ${min}`;
  if (!min && max) return `Until ${max}`;
  return `${min} → ${max}`;
}

export function getFilterDisplayValue(
  filter: { type: string; value: any },
  fieldConfig: SimpleFieldConfig
): string {
  if (filter.type === "dateRange" || filter.type === "timeRange" || filter.type === "numberRange" || filter.type === "range") {
    return formatRange(filter.value);
  } else if (filter.type === "notNull") {
    return "Any value";
  } else if (
    (filter.type === "multiSelect" || filter.type === "exact") &&
    Array.isArray(filter.value)
  ) {
    // For multi-select and exact (Any of OR), show the selected values with their labels
    const selectedOptions = filter.value.map((value: string) => {
      const option = fieldConfig.options?.find(
        (opt: { value: string; label: string }) => opt.value === value
      );
      return option ? option.label : value;
    });
    return selectedOptions.join(", ");
  } else {
    // For single values, try to find the label from options
    if (fieldConfig.options && filter.value && filter.value !== "all") {
      const option = fieldConfig.options.find(
        (opt: { value: string; label: string }) => opt.value === filter.value
      );
      return option ? option.label : String(filter.value);
    }
    // Never render a raw object as "[object Object]".
    if (filter.value && typeof filter.value === "object") {
      return formatRange(filter.value);
    }
    if (filter.value === null || filter.value === undefined || filter.value === "") {
      return "";
    }
    // Entity-reference fields (user/company/contact) are resolved to human names
    // by <FilterChipValue> before this helper runs. If a bare UUID still reaches
    // here it means an unmapped id — never render it raw (no-UUID rule). Callers
    // treat "" as "no displayable value".
    if (looksLikeUuid(filter.value)) {
      return "";
    }
    return String(filter.value);
  }
}
